import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { UserState } from '../user/UserState';
import { WelcomeStorageProvider } from './Welcome';

export type IUserOnboardingStep =
    'viewed-journal-summary' | 'viewed-resources' | 'opened-bea' | 'recording';

export interface IOnboardingStepState {
    startedAt?: string;
    completedAt?: string;
}

interface IUserOnboardingState {
    steps: Partial<Record<IUserOnboardingStep, IOnboardingStepState>>;
    loaded: boolean;
};

export class UserOnboardingState extends State<IUserOnboardingState> {

    static inject = () => [APIClient, UserState, WelcomeStorageProvider];
    constructor(
        private apiClient: APIClient,
        private userState: UserState,
        private welcomeStorage: WelcomeStorageProvider
    ) {
        super({
            steps: {},
            loaded: false
        });

        userState.subscribe(this.load);
    };

    load = async () => {
        let onboardingSteps = {};
        if (this.userState.state.links?.onboarding) {
            const onboarding = await this.apiClient.get(this.userState.state.links.onboarding);
            if (onboarding) {
                onboardingSteps = onboarding.steps;
            }
        }

        this.setState({
            // retain existing steps in case completeStep() was already invoked
            steps: { ...this.state.steps, ...onboardingSteps },
            loaded: true
        });
    };

    private save = async (onboarding: any) => {
        await this.apiClient.put(this.userState.state.links?.onboarding, onboarding);
    };

    isLoaded = () => {
        return Boolean(this.state?.loaded);
    };

    completeStep = async (step: IUserOnboardingStep) => {
        if (!this.isStepComplete(step)) {
            const steps = {
                ...this.state.steps,
                [step]: {
                    ...this.state.steps[step],
                    completedAt: new Date().toISOString()
                }
            };

            this.setState({ steps });
            await this.save({ steps });
        }
    };

    isStepComplete = (step: IUserOnboardingStep) => {
        return Boolean(this.state.steps[step]?.completedAt);
    };

    hasCompletedWelcomeSlides = (): boolean => {
        // Check localStorage (used by anonymous caremap viewers)
        if (this.welcomeStorage.completed.get() === true) {
            return true;
        }
        // Check dynamo state (user may have completed onboarding on another device)
        if (this.isLoaded() && this.isStepComplete('recording')) {
            return true;
        }
        return false;
    };

    // Notify subscribers that welcome slides were completed (for anonymous users using localStorage)
    notifyWelcomeComplete = () => {
        this.setState({});
    };
};
